/**
 * Attribute Type Definitions
 * These constants define the available options for building data attributes
 */

export interface TypeOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Attribute Types — section labels used to group attributes on the
 * building-data widget. Values are stored in `attribute_type_en` on
 * `data.def_building_data_model`; value === label so the stored value is
 * directly human-readable.
 */
export const ATTRIBUTE_TYPES: TypeOption[] = [
  { value: "General building data", label: "General building data" },
  { value: "Construction", label: "Construction" },
  { value: "Roof and facade", label: "Roof and facade" },
  {
    value: "Heating, cooling, ventilation",
    label: "Heating, cooling, ventilation",
  },
  { value: "Technical systems", label: "Technical systems" },
  { value: "Areas and parking", label: "Areas and parking" },
  { value: "Interior finishes", label: "Interior finishes" },
  { value: "Furnishings and equipment", label: "Furnishings and equipment" },
  { value: "Outdoor areas and utilities", label: "Outdoor areas and utilities" },
  { value: "Construction conditions", label: "Construction conditions" },
];

/**
 * Value Types - Data types for attribute values
 */
export const VALUE_TYPES: TypeOption[] = [
  {
    value: "string",
    label: "String",
    description: "Short text value (names, codes, identifiers)",
  },
  {
    value: "text",
    label: "Text",
    description: "Long text value (descriptions, notes)",
  },
  {
    value: "number",
    label: "Number",
    description: "Numeric value (any number, integer or decimal)",
  },
  {
    value: "integer",
    label: "Integer",
    description: "Whole number (no decimals)",
  },
  {
    value: "decimal",
    label: "Decimal",
    description: "Decimal number (floating point)",
  },
  {
    value: "boolean",
    label: "Boolean",
    description: "True/False value",
  },
  {
    value: "date",
    label: "Date",
    description: "Date only (YYYY-MM-DD)",
  },
  {
    value: "datetime",
    label: "DateTime",
    description: "Date and time (YYYY-MM-DD HH:MM:SS)",
  },
  {
    value: "time",
    label: "Time",
    description: "Time only (HH:MM:SS)",
  },
  {
    value: "year",
    label: "Year",
    description: "Year only (YYYY)",
  },
  {
    value: "percentage",
    label: "Percentage",
    description: "Percentage value (0-100)",
  },
  {
    value: "currency",
    label: "Currency",
    description: "Monetary value (EUR, USD, etc.)",
  },
  {
    value: "enum",
    label: "Enum",
    description: "Predefined choice from list",
  },
  {
    value: "json",
    label: "JSON",
    description: "Structured JSON data",
  },
  {
    value: "array",
    label: "Array",
    description: "List of values",
  },
  {
    value: "url",
    label: "URL",
    description: "Web link/URL",
  },
  {
    value: "email",
    label: "Email",
    description: "Email address",
  },
  {
    value: "phone",
    label: "Phone",
    description: "Phone number",
  },
  {
    value: "address",
    label: "Address",
    description: "Physical address",
  },
  {
    value: "coordinate",
    label: "Coordinate",
    description: "Geographic coordinate (latitude/longitude)",
  },
  {
    value: "uuid",
    label: "UUID",
    description: "Unique identifier (UUID format)",
  },
];

/**
 * Measurement Units - Metric units for building attributes
 * Organized by category for better UX
 */
export const MEASUREMENT_UNITS: TypeOption[] = [
  // Length/Distance
  { value: "m", label: "m", description: "Meters (length, height, width)" },
  { value: "cm", label: "cm", description: "Centimeters" },
  { value: "mm", label: "mm", description: "Millimeters" },
  { value: "km", label: "km", description: "Kilometers" },

  // Area
  { value: "m²", label: "m²", description: "Square meters (area)" },
  { value: "cm²", label: "cm²", description: "Square centimeters" },
  { value: "ha", label: "ha", description: "Hectares (land area)" },

  // Volume
  { value: "m³", label: "m³", description: "Cubic meters (volume)" },
  { value: "L", label: "L", description: "Liters (liquid volume)" },
  { value: "mL", label: "mL", description: "Milliliters" },

  // Mass/Weight
  { value: "kg", label: "kg", description: "Kilograms (weight, mass)" },
  { value: "g", label: "g", description: "Grams" },
  { value: "t", label: "t", description: "Tonnes (metric tons)" },

  // Power (Electricity/Heating)
  { value: "W", label: "W", description: "Watts (power)" },
  { value: "kW", label: "kW", description: "Kilowatts (electrical power)" },
  { value: "MW", label: "MW", description: "Megawatts" },

  // Energy
  { value: "kWh", label: "kWh", description: "Kilowatt-hours (energy consumption)" },
  { value: "MWh", label: "MWh", description: "Megawatt-hours" },
  { value: "GJ", label: "GJ", description: "Gigajoules (energy)" },

  // Flow Rate (Water/Gas)
  { value: "L/s", label: "L/s", description: "Liters per second (flow rate)" },
  { value: "L/min", label: "L/min", description: "Liters per minute" },
  { value: "m³/h", label: "m³/h", description: "Cubic meters per hour (gas, water flow)" },
  { value: "m³/s", label: "m³/s", description: "Cubic meters per second" },

  // Pressure
  { value: "Pa", label: "Pa", description: "Pascals (pressure)" },
  { value: "kPa", label: "kPa", description: "Kilopascals" },
  { value: "bar", label: "bar", description: "Bar (pressure)" },

  // Temperature
  { value: "°C", label: "°C", description: "Degrees Celsius (temperature)" },
  { value: "K", label: "K", description: "Kelvin (absolute temperature)" },

  // Currency
  { value: "EUR", label: "EUR", description: "Euros (currency)" },
  { value: "USD", label: "USD", description: "US Dollars" },

  // Time
  { value: "h", label: "h", description: "Hours (time duration)" },
  { value: "min", label: "min", description: "Minutes" },
  { value: "s", label: "s", description: "Seconds" },
  { value: "day", label: "day", description: "Days" },
  { value: "year", label: "year", description: "Years" },

  // Percentage
  { value: "%", label: "%", description: "Percent (percentage)" },

  // Count/Quantity
  { value: "pcs", label: "pcs", description: "Pieces (count)" },
  { value: "units", label: "units", description: "Units (generic count)" },

  // Efficiency/Performance
  {
    value: "kWh/m²",
    label: "kWh/m²",
    description: "Kilowatt-hours per square meter (energy efficiency)",
  },
  {
    value: "kWh/m²·a",
    label: "kWh/m²·a",
    description: "Kilowatt-hours per square meter per year (annual energy)",
  },
  {
    value: "W/m²K",
    label: "W/m²K",
    description: "Watts per square meter kelvin (U-value, thermal transmittance)",
  },
  {
    value: "kg/m³",
    label: "kg/m³",
    description: "Kilograms per cubic meter (density)",
  },
];

/**
 * Category Types - Document classification categories
 */
export const CATEGORY_TYPES: TypeOption[] = [
  {
    value: "document",
    label: "Document",
    description: "Document categories (invoices, contracts, plans, etc.)",
  },
  {
    value: "construction",
    label: "Construction",
    description: "Construction categories (building types, work phases)",
  },
  {
    value: "fire_safety",
    label: "Fire Safety",
    description: "Fire safety categories",
  },
  {
    value: "energy",
    label: "Energy",
    description: "Energy efficiency categories",
  },
  {
    value: "structural",
    label: "Structural",
    description: "Structural categories",
  },
  {
    value: "legal",
    label: "Legal",
    description: "Legal/regulatory categories",
  },
  {
    value: "administrative",
    label: "Administrative",
    description: "Administrative categories",
  },
];

/**
 * Helper functions
 */
export function getAttributeTypeLabel(value: string): string {
  return ATTRIBUTE_TYPES.find((t) => t.value === value)?.label || value;
}

export function getValueTypeLabel(value: string): string {
  return VALUE_TYPES.find((t) => t.value === value)?.label || value;
}

export function getMeasurementUnitLabel(value: string): string {
  return MEASUREMENT_UNITS.find((u) => u.value === value)?.label || value;
}

export function getCategoryTypeLabel(value: string): string {
  return CATEGORY_TYPES.find((t) => t.value === value)?.label || value;
}
